import { selectUserProjectId } from 'core/userContext';
import {
  initialTableSortContextState,
  useTableFacetContext,
  useTablePaginationContext,
  useTableSortContext,
} from 'modules/experiments/contexts';
import {
  fetchExperimentsTableDataRequest,
  fetchExperimentsTableFilterOptionRequest,
} from 'modules/experiments/ducks/actions';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { SharedRouterState } from 'modules/experiments/types';
import { getQueryVariables } from 'modules/experiments/utils';
import { addPositiveToast } from 'modules/toastContainer';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FACET_ACTIONS, useRumAction } from 'services/observability/datadog';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';

import { FullWidth } from 'ui/utility/fullWidth';
import { PageContainer } from '../CommonStyled';
import { ExperimentsIndexHeader } from './sections';
import { ExperimentsIndexFacet } from './sections/facet';
import { ExperimentsIndexPagination, ExperimentsIndexTable } from './sections/table';

export const ExperimentsIndexPage = () => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const { state, pathname } = useLocation<SharedRouterState>();
  const navigate = useNavigate();
  const addSignal = useRumAction(FACET_ACTIONS);
  const projectId = useSelector(selectUserProjectId);
  const { search, selectedFilters, selectedTab } = useTableFacetContext();
  const { page, pageSize } = useTablePaginationContext();
  const { sortBy, sortDirection } = useTableSortContext();
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const queryVariables = getQueryVariables({
    selectedFilters,
    selectedTab,
    pageSize,
    page,
    sortBy,
    sortDirection,
    search,
  });

  // Load all filter options on page load
  useEffect(() => {
    if (projectId) {
      dispatch(fetchExperimentsTableFilterOptionRequest({ projectId, isWhatsAppFFOn }));
    }
  }, [projectId]);

  // Loads fresh table data on changes in the FE
  useEffect(() => {
    dispatch(fetchExperimentsTableDataRequest(queryVariables));
  }, [JSON.stringify(queryVariables)]);

  useEffect(() => {
    if (search) {
      addSignal('INDEX_SEARCHED', {
        searchTerm: search,
      });
    }
  }, [search]);

  useEffect(() => {
    if (
      sortBy !== initialTableSortContextState.sortBy ||
      sortDirection !== initialTableSortContextState.sortDirection
    ) {
      addSignal('INDEX_SORTED', {
        sortBy,
      });
    }
  }, [sortBy]);

  useEffect(() => {
    if (selectedFilters.length) {
      addSignal('INDEX_FILTERED', {
        filterCount: selectedFilters.length,
      });
    }
  }, [selectedFilters.length]);

  // Show the delete toaster when the page redirects with an experiment name.
  // This means that an experiment was successfully deleted.
  useEffect(() => {
    if (state?.experimentName) {
      const successToasterMessage = t('successMessage.deleteExperiment', {
        experiment: state?.experimentName,
      });
      dispatch(addPositiveToast(successToasterMessage));
      // Delete the passed state.
      navigate(pathname, {});
    }
  }, [state?.experimentName]);

  return (
    <PageContainer>
      <ExperimentsIndexHeader />
      <ExperimentsIndexFacet />
      <FullWidth>
        <ExperimentsIndexTable />
        <ExperimentsIndexPagination />
      </FullWidth>
    </PageContainer>
  );
};
